import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import PopupModal from '../components/PopupModal';
import WebinarUpsellModal from '../components/WebinarUpsellModal';
import GroupConsultationUpsellModal from '../components/GroupConsultationUpsellModal';

// Updated Dec 1 2024: All payments now use on-site checkout
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    consent: false
  });
  const [loading, setLoading] = useState(false);
  
  // Webinar upsell modal state
  const [showWebinarUpsell, setShowWebinarUpsell] = useState(false);
  
  // Group consultation upsell modal state
  const [showGroupUpsell, setShowGroupUpsell] = useState(false);

  // Bottom free guide form state
  const [freeGuideForm, setFreeGuideForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    consent: false
  });
  const [freeGuideLoading, setFreeGuideLoading] = useState(false);
  const [freeGuideSuccess, setFreeGuideSuccess] = useState(false);

  // Waitlist form state
  const [waitlistForm, setWaitlistForm] = useState({
    firstName: '',
    lastName: '',
    email: ''
  });
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [waitlistSuccess, setWaitlistSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFreeGuideChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFreeGuideForm({
      ...freeGuideForm,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.consent) {
      alert('Please agree to receive communications to continue.');
      return;
    }
    
    setLoading(true);

    try {
      const bookingData = {
        service_type: 'Free Discovery Call',
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: null,
        message: null,
        preferred_date: null
      };

      await axios.post(`${API}/bookings`, bookingData);

      // Navigate to thank you page with upsell flag
      navigate('/thank-you', {
        state: {
          serviceName: 'Free Discovery Call',
          servicePrice: 'Free',
          email: formData.email,
          userName: formData.name,
          showUpsell: true,
          isFirstPurchase: true
        }
      });
    } catch (err) {
      console.error('Booking error:', err);
      alert('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  const handleFreeGuideSubmit = async (e) => {
    e.preventDefault();
    
    if (!freeGuideForm.consent) {
      alert('Please agree to receive communications to continue.');
      return;
    }
    
    if (!freeGuideForm.firstName || !freeGuideForm.lastName || !freeGuideForm.email) {
      alert('Please fill in all fields.');
      return;
    }
    
    setFreeGuideLoading(true);

    try {
      await axios.post(`${API}/free-guide-leads`, {
        first_name: freeGuideForm.firstName,
        last_name: freeGuideForm.lastName,
        email: freeGuideForm.email
      });

      setFreeGuideSuccess(true);
      setFreeGuideForm({
        firstName: '',
        lastName: '',
        email: '',
        consent: false
      });

      // Reset success message after 5 seconds
      setTimeout(() => {
        setFreeGuideSuccess(false);
      }, 5000);
    } catch (err) {
      console.error('Free guide submission error:', err);
      alert('Something went wrong. Please try again.');
    } finally {
      setFreeGuideLoading(false);
    }
  };

  const handleWaitlistChange = (e) => {
    const { name, value } = e.target;
    setWaitlistForm({
      ...waitlistForm,
      [name]: value
    });
  };

  const handleWaitlistSubmit = async (e, source = 'group-consultation') => {
    e.preventDefault();
    
    if (!waitlistForm.firstName || !waitlistForm.lastName || !waitlistForm.email) {
      alert('Please fill in all fields.');
      return;
    }
    
    setWaitlistLoading(true);

    try {
      const eventName = source === 'webinar' ? 'Free Webinar - Home Care Industry 101' : 'Group Consultation - Home Care Agency';
      
      await axios.post(`${API}/waitlist`, {
        first_name: waitlistForm.firstName,
        last_name: waitlistForm.lastName,
        email: waitlistForm.email,
        event: eventName
      });

      setWaitlistSuccess(true);
      setWaitlistForm({
        firstName: '',
        lastName: '',
        email: '',
        source: source
      });

      // Close modal after 3 seconds
      setTimeout(() => {
        setShowWaitlistModal(false);
        setWaitlistSuccess(false);
      }, 3000);
    } catch (err) {
      console.error('Waitlist submission error:', err);
      alert('Something went wrong. Please try again.');
    } finally {
      setWaitlistLoading(false);
    }
  };

  const goToCheckout = (productId) => {
    navigate(`/checkout?product=${productId}`);
  };

  const handleServiceRequest = async (service) => {
    const name = prompt('Enter your name:');
    const email = prompt('Enter your email:');
    const phone = prompt('Enter your phone number:');
    
    if (!name || !email || !phone) return;

    try {
      const bookingData = {
        service_type: service.title,
        name,
        email,
        phone,
        company: null,
        message: null,
        preferred_date: null
      };

      await axios.post(`${API}/bookings`, bookingData);

      navigate('/thank-you', {
        state: {
          serviceName: service.title,
          servicePrice: service.price,
          email: email,
          showUpsell: false,
          isFirstPurchase: false
        }
      });
    } catch (err) {
      console.error('Booking error:', err);
      alert('Something went wrong. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-black text-white relative overflow-hidden">
      {/* Background Logo Watermark */}
      <div className="fixed top-1/2 right-0 w-2/3 h-2/3 opacity-15 pointer-events-none flex items-center justify-end pr-10" style={{ transform: 'translateY(-50%) rotate(-12deg)' }}>
        <img 
          src="https://customer-assets.emergentagent.com/job_pdf-to-site-6/artifacts/jfbpb7og_Solutions%20Logo%20%282%29.png" 
          alt="" 
          className="w-full h-full object-contain"
          style={{ mixBlendMode: 'screen' }}
        />
      </div>

      {/* Elegant Header */}
      <header className="fixed top-0 w-full bg-black/80 backdrop-blur-md border-b border-gray-700 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_pdf-to-site-6/artifacts/jfbpb7og_Solutions%20Logo%20%282%29.png" 
              alt="Ability Healthcare Solutions" 
              className="h-12 w-auto"
              style={{ 
                mixBlendMode: 'screen'
              }}
            />
            <div className="text-left">
              <div className="text-sm uppercase tracking-wider text-gray-300 font-bold">Turning Challenges</div>
              <div className="text-sm uppercase tracking-wider text-blue-400 font-bold">Into Solutions</div>
            </div>
          </div>
        </div>
      </header>

      {/* Free Live Webinar Section - ABSOLUTE FIRST */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-green-700 to-emerald-800">
        <div className="max-w-5xl mx-auto">
          <div className="text-center">
            <h2 className="text-4xl md:text-5xl font-black mb-4 text-white">Free Live Webinar: Home Care Industry 101</h2>
            <p className="text-green-100 text-xl mb-8 max-w-3xl mx-auto">
              Learn how the home care industry works before you invest time or money. This session helps you understand licensing, setup, and what to expect when starting a home care agency. You get direct answers to your questions with no sales pressure.
            </p>

            <div className="bg-black/30 rounded-lg p-8 mb-8 text-left max-w-2xl mx-auto">
              <h3 className="text-2xl font-bold mb-6 text-green-300 text-center">What You'll Learn:</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <span className="text-green-400 mr-3 text-2xl flex-shrink-0">•</span>
                  <span className="text-gray-200 text-lg">What a home care agency does</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-400 mr-3 text-2xl flex-shrink-0">•</span>
                  <span className="text-gray-200 text-lg">Startup requirements</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-400 mr-3 text-2xl flex-shrink-0">•</span>
                  <span className="text-gray-200 text-lg">Common mistakes to avoid</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-400 mr-3 text-2xl flex-shrink-0">•</span>
                  <span className="text-gray-200 text-lg">Live Q&A</span>
                </li>
              </ul>
            </div>

            <div className="flex justify-center items-center gap-3 mb-8">
              <span className="text-6xl font-black text-green-300">FREE</span>
              <span className="text-green-100 text-xl">No Cost • No Commitment • No Sales Pitch</span>
            </div>

            {/* Webinar Waitlist Form */}
            <div className="bg-gradient-to-br from-green-600 to-green-800 rounded-xl p-8 max-w-xl mx-auto border-2 border-green-400">
              {waitlistSuccess && waitlistForm.source === 'webinar' ? (
                <div className="text-center py-4">
                  <div className="mb-3">
                    <svg className="w-12 h-12 text-white mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h4 className="text-2xl font-bold text-white mb-2">You're on the Waitlist!</h4>
                  <p className="text-green-100">
                    We'll notify you when registration opens for the next webinar.
                  </p>
                </div>
              ) : (
                <>
                  <h4 className="text-2xl font-bold text-white mb-2 text-center">Join the Webinar Waitlist</h4>
                  <p className="text-green-100 text-center mb-6">
                    Be the first to know when we announce the next free webinar session.
                  </p>

                  <form onSubmit={(e) => { e.preventDefault(); handleWaitlistSubmit(e, 'webinar'); }} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <input
                        type="text"
                        name="firstName"
                        value={waitlistForm.firstName}
                        onChange={handleWaitlistChange}
                        required
                        className="w-full px-4 py-3 border border-green-300 rounded-lg focus:ring-2 focus:ring-white focus:border-transparent bg-white text-gray-900 placeholder-gray-500"
                        placeholder="First Name"
                      />
                      <input
                        type="text"
                        name="lastName"
                        value={waitlistForm.lastName}
                        onChange={handleWaitlistChange}
                        required
                        className="w-full px-4 py-3 border border-green-300 rounded-lg focus:ring-2 focus:ring-white focus:border-transparent bg-white text-gray-900 placeholder-gray-500"
                        placeholder="Last Name"
                      />
                    </div>

                    <input
                      type="email"
                      name="email"
                      value={waitlistForm.email}
                      onChange={handleWaitlistChange}
                      required
                      className="w-full px-4 py-3 border border-green-300 rounded-lg focus:ring-2 focus:ring-white focus:border-transparent bg-white text-gray-900 placeholder-gray-500"
                      placeholder="Email Address"
                    />

                    <button
                      type="submit"
                      disabled={waitlistLoading}
                      className="w-full bg-white text-green-700 px-8 py-4 rounded-full font-bold hover:bg-green-50 transition-all text-lg uppercase tracking-wide shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {waitlistLoading ? 'Joining...' : 'Join Waitlist →'}
                    </button>
                  </form>

                  <p className="text-xs text-green-200 text-center mt-4">
                    We'll only contact you about the next free webinar.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Hero Section with Discovery Call */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 relative">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Headline & Stats */}
            <div className="text-center">
              <div className="mb-6">
                <div className="inline-block bg-gradient-to-r from-blue-600 to-green-600 text-white px-6 py-2 rounded-full text-sm font-bold uppercase tracking-wide">
                  Veteran & Nurse-Led Excellence
                </div>
              </div>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-black mb-6 leading-tight">
                Stop Guessing. Build a Home Care Agency That <span className="bg-gradient-to-r from-blue-400 via-green-400 to-blue-500 bg-clip-text text-transparent">Actually Works.</span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed">
                Get the exact blueprint to launch, license, and grow your business—with less stress and more profit.
              </p>
              
              {/* Social Proof */}
              <div className="bg-gradient-to-r from-blue-900/30 to-green-900/30 border border-blue-500/30 rounded-xl p-6 mb-8">
                <div className="grid grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="text-4xl font-bold text-blue-400">10K+</div>
                    <div className="text-xs text-gray-400 uppercase tracking-wide">Clients Served</div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-bold text-green-400">6→7 Fig</div>
                    <div className="text-xs text-gray-400 uppercase tracking-wide">Growth Achieved</div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-bold text-blue-400">100%</div>
                    <div className="text-xs text-gray-400 uppercase tracking-wide">Proven Systems</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Book Discovery Call CTA */}
            <div id="book" className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-12 border-2 border-blue-500 shadow-2xl text-center" data-testid="discovery-form">
              <div className="mb-8">
                <div className="inline-block bg-green-600 text-white px-6 py-3 rounded-full text-lg font-bold mb-6 uppercase">100% Free</div>
                <h2 className="text-4xl font-bold mb-4">Book Your Discovery Call</h2>
                <p className="text-xl text-gray-300 mb-2">15 Minutes • No Obligation • Real Results</p>
                <p className="text-gray-400">Choose your convenient time on the next page</p>
              </div>

              <Link
                to="/free-call"
                className="inline-block w-full bg-gradient-to-r from-blue-600 to-green-600 text-white px-8 py-6 rounded-lg text-2xl font-bold hover:from-blue-700 hover:to-green-700 transition-all shadow-2xl uppercase tracking-wide"
                data-testid="submit-discovery-btn"
              >
                Schedule Your Free Call →
              </Link>

              <p className="text-center text-sm text-gray-400 mt-6">
                🔒 No credit card required • Book instantly
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-black/40">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black mb-4">What Clients Are Saying</h2>
            <p className="text-xl text-gray-400">Real Results From Real Agency Owners</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-8 border border-gray-700 shadow-xl" data-testid="testimonial-1">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <p className="text-gray-300 mb-6 text-lg leading-relaxed">
                "We went from struggling to break even to hitting $500K in revenue in just 18 months. The systems and strategies are pure gold."
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-xl font-bold mr-4">JM</div>
                <div>
                  <div className="font-bold">Jennifer Martinez</div>
                  <div className="text-sm text-gray-400">Home Care Agency Owner, TX</div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-8 border border-gray-700 shadow-xl" data-testid="testimonial-2">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <p className="text-gray-300 mb-6 text-lg leading-relaxed">
                "Finally found a consultant who actually knows the home care business inside and out. Not just theory – real, practical solutions that work."
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center text-xl font-bold mr-4">DK</div>
                <div>
                  <div className="font-bold">David Kim</div>
                  <div className="text-sm text-gray-400">Healthcare Entrepreneur, CA</div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-8 border border-gray-700 shadow-xl" data-testid="testimonial-3">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <p className="text-gray-300 mb-6 text-lg leading-relaxed">
                "The best investment in my business. Went from 12 clients to 85 clients in 6 months. The referral strategies alone paid for everything 10x over."
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-xl font-bold mr-4">ST</div>
                <div>
                  <div className="font-bold">Sarah Thompson</div>
                  <div className="text-sm text-gray-400">RN, Agency Founder, FL</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Premium Services Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black mb-4">
              Level-Up Your Empire With the Only Business Mentor Programs You'll Ever Need
            </h2>
            <p className="text-xl text-gray-400">Choose Your Path to 7-Figure Success</p>
          </div>

          <div className="space-y-6">
            {/* Group Consultation - January 3rd */}
            <div className="bg-gradient-to-r from-blue-800 to-blue-900 rounded-xl p-8 border-2 border-blue-400 shadow-2xl relative overflow-hidden">
              <div className="text-center">
                <h3 className="text-3xl font-bold mb-3 text-white">Start My Home Care Agency</h3>
                <p className="text-blue-100 text-lg mb-6">Join our exclusive group consultation and learn everything you need to successfully launch your home care agency.</p>
                
                <div className="flex justify-center items-baseline gap-3 mb-2">
                  <span className="text-2xl text-blue-300 line-through">$1,500 Value</span>
                </div>
                <div className="flex justify-center items-baseline gap-3 mb-3">
                  <span className="text-5xl font-black text-white">$399</span>
                </div>
                <div className="bg-green-500 text-white px-6 py-3 rounded-lg inline-block font-bold mb-4 text-lg">
                  ✅ LIMITED SEATS AVAILABLE
                </div>
                <p className="text-blue-200 text-lg font-semibold mb-6">📅 January 3rd, 2025 | ⏰ 1:00 PM EST</p>

                <div className="bg-black/30 rounded-lg p-6 mb-6 text-left max-w-3xl mx-auto">
                  <h4 className="text-xl font-bold mb-4 text-blue-300 text-center">What You'll Learn:</h4>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <span className="text-green-400 mr-3 text-xl flex-shrink-0">✨</span>
                      <span className="text-white">How to structure your business the right way the first time</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-400 mr-3 text-xl flex-shrink-0">✨</span>
                      <span className="text-white">How to apply for the correct NPI type for your agency</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-400 mr-3 text-xl flex-shrink-0">✨</span>
                      <span className="text-white">Which insurance credentials are required for licensing</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-400 mr-3 text-xl flex-shrink-0">✨</span>
                      <span className="text-white">A real example of compliant Policies & Procedures</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-400 mr-3 text-xl flex-shrink-0">✨</span>
                      <span className="text-white">How to become contracted with major commercial insurance providers</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-400 mr-3 text-xl flex-shrink-0">✨</span>
                      <span className="text-white">How to get approved as a VA community care provider</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-400 mr-3 text-xl flex-shrink-0">✨</span>
                      <span className="text-white">Medicaid & Medicare insider tips most agencies never share</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-400 mr-3 text-xl flex-shrink-0">✨</span>
                      <span className="text-white">Staffing and system needs</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-400 mr-3 text-xl flex-shrink-0">✨</span>
                      <span className="text-white">Plus more insider secrets!</span>
                    </li>
                  </ul>
                </div>

                <button
                  onClick={() => setShowGroupUpsell(true)}
                  className="bg-green-500 text-white px-12 py-5 rounded-full font-black hover:bg-green-400 transition-all text-xl uppercase tracking-wide shadow-2xl inline-block"
                  data-testid="group-consultation-btn"
                >
                  Reserve Your Seat Now - $399 →
                </button>
                
                <p className="text-blue-200 text-sm mt-4">
                  🔒 Secure Payment • Limited Availability
                </p>
              </div>
            </div>

            {/* Power Session */}
            <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-xl p-8 border-2 border-gray-700 hover:border-blue-500 transition-all shadow-2xl">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                <div className="flex-1">
                  <h3 className="text-3xl font-bold mb-3">Pick My Brain Power Session</h3>
                  <p className="text-gray-300 text-lg mb-3">30 minutes of laser-focused consulting on your specific challenge. Get unstuck fast with expert guidance.</p>
                  <div className="flex items-baseline gap-3">
                    <span className="text-4xl font-black text-blue-400">$99</span>
                    <span className="text-gray-400">30 Minutes</span>
                  </div>
                </div>
                <button
                  onClick={() => goToCheckout('power-session')}
                  className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-10 py-4 rounded-full font-bold hover:from-blue-700 hover:to-blue-800 transition-all whitespace-nowrap text-lg uppercase tracking-wide shadow-xl text-center"
                  data-testid="power-session-btn"
                >
                  Book Now →
                </button>
              </div>
            </div>

            {/* Comprehensive */}
            <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-xl p-8 border-2 border-gray-700 hover:border-green-500 transition-all shadow-2xl">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                <div className="flex-1">
                  <h3 className="text-3xl font-bold mb-3">Comprehensive Consultation</h3>
                  <p className="text-gray-300 text-lg mb-3">Intense session covering every aspect of starting and running a successful home care agency.</p>
                  <div className="flex items-baseline gap-3">
                    <span className="text-4xl font-black text-green-400">$2,499</span>
                    <span className="text-xl text-gray-400 line-through">$3,500</span>
                    <span className="text-gray-400">Intense Session</span>
                  </div>
                </div>
                <button
                  onClick={() => goToCheckout('comprehensive-consultation')}
                  className="bg-gradient-to-r from-green-600 to-green-700 text-white px-10 py-4 rounded-full font-bold hover:from-green-700 hover:to-green-800 transition-all whitespace-nowrap text-lg uppercase tracking-wide shadow-xl text-center"
                  data-testid="comprehensive-btn"
                >
                  Book Now →
                </button>
              </div>
            </div>

            {/* Agency Build - FEATURED */}
            <div className="bg-gradient-to-r from-blue-900 to-green-900 rounded-xl p-8 border-2 border-blue-400 shadow-2xl relative overflow-hidden">
              <div className="absolute top-4 right-4">
                <span className="bg-yellow-400 text-black px-4 py-2 rounded-full text-sm font-bold uppercase">Most Popular</span>
              </div>
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                <div className="flex-1">
                  <h3 className="text-3xl font-bold mb-3">Agency Build & Strategy Walkthrough</h3>
                  <p className="text-gray-200 text-lg mb-3">8 hours of hands-on support to build your agency from the ground up with proven 7-figure systems.</p>
                  <div className="flex items-baseline gap-3 mb-4">
                    <span className="text-5xl font-black text-yellow-400">$4,999</span>
                    <span className="text-gray-300">8 Total Hours</span>
                  </div>
                  <ul className="grid grid-cols-2 gap-2 text-sm text-gray-300">
                    <li className="flex items-center"><span className="text-green-400 mr-2">✓</span> Complete Setup</li>
                    <li className="flex items-center"><span className="text-green-400 mr-2">✓</span> Marketing Automation</li>
                    <li className="flex items-center"><span className="text-green-400 mr-2">✓</span> Systems Implementation</li>
                    <li className="flex items-center"><span className="text-green-400 mr-2">✓</span> Ongoing Support</li>
                  </ul>
                </div>
                <button
                  onClick={() => goToCheckout('agency-build')}
                  className="bg-yellow-400 text-black px-10 py-4 rounded-full font-black hover:bg-yellow-300 transition-all whitespace-nowrap text-lg uppercase tracking-wide shadow-2xl text-center"
                  data-testid="agency-build-btn"
                >
                  Get Started →
                </button>
              </div>
            </div>

            {/* Built For You Agency */}
            <div className="bg-gradient-to-r from-purple-900 to-blue-900 rounded-xl p-8 border-2 border-purple-400 shadow-2xl relative overflow-hidden">
              <div className="absolute top-4 right-4">
                <span className="bg-purple-400 text-black px-4 py-2 rounded-full text-sm font-bold uppercase">Turnkey Solution</span>
              </div>
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                <div className="flex-1">
                  <h3 className="text-3xl font-bold text-white mb-3">Built For You Home Care Agency</h3>
                  <p className="text-purple-100 text-lg mb-3">Fully licensed, ready-to-operate home care agency built for you. Skip months of setup and start serving clients immediately.</p>
                  <div className="flex items-baseline gap-3 mb-4">
                    <span className="text-5xl font-black text-purple-300">$14,999</span>
                  </div>
                  <ul className="grid grid-cols-2 gap-2 text-sm text-purple-100">
                    <li className="flex items-center"><span className="text-purple-300 mr-2">✓</span> Already Licensed</li>
                    <li className="flex items-center"><span className="text-purple-300 mr-2">✓</span> Ready to Operate</li>
                    <li className="flex items-center"><span className="text-purple-300 mr-2">✓</span> All Documents Included</li>
                    <li className="flex items-center"><span className="text-purple-300 mr-2">✓</span> Start Immediately</li>
                  </ul>
                </div>
                <button
                  onClick={() => goToCheckout('built-agency')}
                  className="bg-purple-400 text-black px-10 py-4 rounded-full font-black hover:bg-purple-300 transition-all whitespace-nowrap text-lg uppercase tracking-wide shadow-2xl text-center"
                  data-testid="built-agency-btn"
                >
                  Get Started →
                </button>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Resources - Direct Purchase */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-black/40">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black mb-4">Done-For-You Resources</h2>
            <p className="text-xl text-gray-400">Instant Access to Premium Guides</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-8 border-2 border-blue-400 shadow-2xl">
              <h3 className="text-2xl font-bold mb-3 text-blue-400">Licensed or Unlicensed: A 50-State Home Care Licensing Guide</h3>
              <p className="text-gray-300 mb-4">Understand exactly what's required to open a home care agency in any U.S. state. This guide breaks down every state's licensing requirements, application fees, and department links — all in one organized resource.</p>
              
              <div className="bg-gray-700/50 rounded-lg p-4 mb-4">
                <p className="text-sm font-semibold text-gray-200 mb-3">You'll learn:</p>
                <ul className="space-y-2 text-sm text-gray-300">
                  <li className="flex items-start">
                    <span className="text-blue-400 mr-2">✓</span>
                    <span>Which states require a home care license</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-400 mr-2">✓</span>
                    <span>Which states allow unlicensed non-medical agencies</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-400 mr-2">✓</span>
                    <span>Certificate of Need (CON) states</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-400 mr-2">✓</span>
                    <span>Direct links to each state's health department and application forms</span>
                  </li>
                </ul>
              </div>
              
              <p className="text-xs text-gray-400 italic mb-4">Perfect for entrepreneurs starting a home care agency, consultants helping others launch, or existing owners expanding into new states.</p>
              
              <div className="text-4xl font-black text-blue-400 mb-6">$79</div>
              <button
                onClick={() => goToCheckout('licensing-guide')}
                className="block w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-4 rounded-lg font-bold hover:from-blue-700 hover:to-blue-800 transition-all text-center text-lg uppercase tracking-wide shadow-xl"
                data-testid="licensing-guide-btn"
              >
                Get Instant Access →
              </button>
            </div>

            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-8 border-2 border-blue-500 shadow-2xl">
              <h3 className="text-2xl font-bold mb-3">Referral Partner Database</h3>
              <p className="text-gray-300 mb-4">Access a curated list of over 300 verified healthcare, insurance, and workers' compensation referral partners to help your agency get in-network and approved for referrals faster. This powerful database is designed for home care, home health, and medical service providers ready to expand their referral network, secure contracts, and grow their client base with trusted industry connections.</p>
              <div className="text-4xl font-black text-blue-400 mb-6">$149.99</div>
              <button
                onClick={() => goToCheckout('referral-database')}
                className="block w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-4 rounded-lg font-bold hover:from-blue-700 hover:to-blue-800 transition-all text-center text-lg uppercase tracking-wide shadow-xl"
                data-testid="referral-list-btn"
              >
                Get Instant Access →
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-black mb-6">
            Ready to Build Your 7-Figure Agency?
          </h2>
          <p className="text-2xl text-gray-300 mb-10">
            Start with a free discovery call and see if this is the right fit.
          </p>
          <Link
            to="/free-call"
            className="inline-block bg-gradient-to-r from-blue-600 to-green-600 text-white px-12 py-6 rounded-full text-2xl font-black hover:from-blue-700 hover:to-green-700 transition-all transform hover:scale-105 shadow-2xl uppercase tracking-wide"
            data-testid="final-cta-btn"
          >
            Book Free Call Now →
          </Link>
          <p className="text-gray-400 mt-6">Join 10,000+ successful agency owners</p>
        </div>
      </section>

      {/* Bottom Free Guide Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-900 via-purple-900 to-blue-900">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-2">
              {/* Left side - Image */}
              <div className="relative h-64 md:h-auto">
                <img
                  src="https://customer-assets.emergentagent.com/job_pdf-to-site-6/artifacts/0g2xfsnj_SHT%20PIC.png"
                  alt="Start Your Home Care Business Guide"
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Right side - Form */}
              <div className="p-8 md:p-10">
                {freeGuideSuccess ? (
                  <div className="text-center py-8">
                    <div className="mb-4">
                      <svg className="w-16 h-16 text-green-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Success!</h3>
                    <p className="text-gray-600">
                      Check your email - your free guide is on the way!
                    </p>
                  </div>
                ) : (
                  <>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">
                      Get Your FREE Guide
                    </h2>
                    <p className="text-gray-600 mb-6">
                      Start your home care business the right way with our comprehensive guide.
                    </p>
                    
                    <form onSubmit={handleFreeGuideSubmit} className="space-y-4">
                      <div>
                        <input
                          type="text"
                          name="firstName"
                          value={freeGuideForm.firstName}
                          onChange={handleFreeGuideChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                          placeholder="First Name"
                        />
                      </div>

                      <div>
                        <input
                          type="text"
                          name="lastName"
                          value={freeGuideForm.lastName}
                          onChange={handleFreeGuideChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                          placeholder="Last Name"
                        />
                      </div>

                      <div>
                        <input
                          type="email"
                          name="email"
                          value={freeGuideForm.email}
                          onChange={handleFreeGuideChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                          placeholder="Email Address"
                        />
                      </div>

                      <div className="flex items-start">
                        <input
                          type="checkbox"
                          name="consent"
                          checked={freeGuideForm.consent}
                          onChange={handleFreeGuideChange}
                          required
                          className="mt-1 h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <label className="ml-3 text-sm text-gray-700">
                          I agree to receive communications and accept the{' '}
                          <Link to="/terms" className="text-blue-600 hover:underline">Terms</Link>
                          {' '}&{' '}
                          <Link to="/privacy-policy" className="text-blue-600 hover:underline">Privacy Policy</Link>
                        </label>
                      </div>

                      <button
                        type="submit"
                        disabled={freeGuideLoading}
                        className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-4 rounded-full font-bold hover:from-blue-700 hover:to-blue-800 transition-all text-lg uppercase tracking-wide shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {freeGuideLoading ? 'Sending...' : 'Get My Free Guide →'}
                      </button>
                    </form>
                    
                    <p className="text-xs text-gray-500 text-center mt-4">
                      We respect your privacy. Unsubscribe at any time.
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Elegant Footer */}
      <footer className="bg-black border-t border-gray-800 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto text-center">
          <div className="flex flex-col items-center justify-center mb-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_pdf-to-site-6/artifacts/jfbpb7og_Solutions%20Logo%20%282%29.png" 
              alt="Ability Healthcare Solutions" 
              className="h-20 w-auto mb-4"
              style={{ 
                mixBlendMode: 'screen'
              }}
            />
            <div className="text-center">
              <div className="text-base uppercase tracking-wider text-gray-300 font-bold">Turning Challenges</div>
              <div className="text-base uppercase tracking-wider text-blue-400 font-bold">Into Solutions</div>
            </div>
          </div>
          <p className="text-gray-400 text-sm mb-4">
            &copy; {new Date().getFullYear()} Ability Healthcare Solutions. All rights reserved.
          </p>
          <div className="space-x-4">
            <Link to="/privacy-policy" className="text-gray-400 hover:text-white text-sm transition-colors">Privacy Policy</Link>
            <span className="text-gray-600">|</span>
            <Link to="/terms" className="text-gray-400 hover:text-white text-sm transition-colors">Terms & Conditions</Link>
          </div>
        </div>
      </footer>

      {/* Pop-up Modal - Free Guide */}
      <PopupModal />
      
      {/* Webinar Upsell Modal */}
      <WebinarUpsellModal 
        isOpen={showWebinarUpsell}
        onClose={() => setShowWebinarUpsell(false)}
        webinarUrl="https://zoom.us/meeting/register/fiROs28wS9ywUFayDANtmA"
      />
      
      {/* Group Consultation Upsell Modal */}
      <GroupConsultationUpsellModal 
        isOpen={showGroupUpsell}
        onClose={() => setShowGroupUpsell(false)}
        onContinueToCheckout={() => goToCheckout('group-consultation')}
      />
    </div>
  );
};

export default Home;